import { DlfMatcherTypes } from "../../types/rules/DlfMatcherTypes";
import {
  AllowEmptyDlfMatcher,
  AllowEmptyDlfMatcherProperties,
} from "./AllowEmptyDlfMatcher";
import { MatcherFactory } from "./factory/MatcherFactory";
import { FuzzyFieldMatcher } from "./FuzzyFieldMatcher";
import { SectionMatcher, SectionMatcherData } from "./SectionMatcher";

type KeyValueMatcherData = {
  keyMatcher: SectionMatcherData;
  valueMatcher: SectionMatcherData;
} & AllowEmptyDlfMatcherProperties;

class KeyValueMatcher
  extends AllowEmptyDlfMatcher
  implements KeyValueMatcherData
{
  discriminator: DlfMatcherTypes = "F_KEYVALUE";
  /* KeyValue Matcher properties */
  keyMatcher: SectionMatcher;
  valueMatcher: SectionMatcher;
  /* Allow Empty Matcher properties */
  allowEmpty: boolean;
  /* Dlf Matcher property */
  fieldName: string;
  position: number;
  threshold: number;
  /* Matcher properties */
  reusable: boolean;
  ocrConfidenceThreshold: number;

  constructor(data: KeyValueMatcherData) {
    super();
    this.keyMatcher = MatcherFactory.createDlfMatcher(
      data.keyMatcher,
    ) as SectionMatcher;
    this.valueMatcher = MatcherFactory.createDlfMatcher(
      data.valueMatcher,
    ) as SectionMatcher;

    this.fieldName = data.fieldName;
    this.keyMatcher.fieldName = this.fieldName;
    this.valueMatcher.fieldName = this.fieldName;
    this.keyMatcher.matcher.fieldName = this.fieldName;
    this.valueMatcher.matcher.fieldName = this.fieldName;

    this.threshold = data.threshold;
    // Avoid overriding FuzzyFieldMatcher threshold
    if (!(this.keyMatcher.matcher instanceof FuzzyFieldMatcher)) {
      this.keyMatcher.threshold = this.keyMatcher.matcher.threshold;
    }
    this.valueMatcher.threshold = this.valueMatcher.matcher.threshold;

    this.reusable = data.reusable ?? true;
    this.position = data.position;
    this.allowEmpty = data.allowEmpty ?? false;
    this.ocrConfidenceThreshold = data.ocrConfidenceThreshold;
    // set sectionMatcher and sectionMatcher.matcher to same threshold, from sectionMatcher.matcher
    this.keyMatcher.ocrConfidenceThreshold = this.ocrConfidenceThreshold;
    this.keyMatcher.matcher.ocrConfidenceThreshold =
      this.ocrConfidenceThreshold;
    this.valueMatcher.ocrConfidenceThreshold = this.ocrConfidenceThreshold;
    this.valueMatcher.matcher.ocrConfidenceThreshold =
      this.ocrConfidenceThreshold;
  }

  setAllowEmpty(value: boolean) {
    this.allowEmpty = value;
    this.keyMatcher.setAllowEmpty(value);
    this.valueMatcher.setAllowEmpty(value);
  }

  isConfigured(): boolean {
    return (
      this.keyMatcher.matcher.isConfigured() &&
      this.keyMatcher.section.isConfigured()
    );
  }

  clear() {
    this.keyMatcher.clear();
    this.valueMatcher.clear();
  }

  toMatcherData(): KeyValueMatcherData {
    return {
      ocrConfidenceThreshold: this.ocrConfidenceThreshold,
      discriminator: this.discriminator,
      fieldName: this.fieldName,
      keyMatcher: this.keyMatcher.toMatcherData(),
      valueMatcher: this.valueMatcher.toMatcherData(),
      position: this.position,
      reusable: this.reusable,
      allowEmpty: this.allowEmpty,
      threshold: this.threshold,
    };
  }

  getDiscriminator(): DlfMatcherTypes {
    return this.discriminator;
  }

  getMatcherId(): string {
    return `${this.discriminator}-${this.threshold}-${this.ocrConfidenceThreshold}-[${this.keyMatcher.getMatcherId()}]-[${this.valueMatcher.getMatcherId()}]`;
  }

  static createTest(rest: Partial<KeyValueMatcherData> = {}): KeyValueMatcher {
    const data: KeyValueMatcherData = {
      ocrConfidenceThreshold: -1,
      keyMatcher: SectionMatcher.createTest({}),
      valueMatcher: SectionMatcher.createTest({}),
      discriminator: "F_KEYVALUE",
      fieldName: "",
      position: 0,
      reusable: true,
      allowEmpty: false,
      threshold: 0,
    };

    return new KeyValueMatcher({
      ...data,
      ...rest,
    });
  }
}

export { KeyValueMatcher, KeyValueMatcherData };
